import { useEffect, useMemo, useState } from 'react'
import { get } from '../lib/api'

/**
 * Dial a command instead of remembering one.
 *
 * ── What this is ─────────────────────────────────────────────────────────────
 *
 * The command line is fast once you know the grammar and a dead end before
 * that: "C01 done" is obvious in hindsight and unguessable in advance, and the
 * only way in was typing `help` and reading a wall of syntax.
 *
 * So the grammar is laid out as keys. Pick a verb, pick a row, pick a value,
 * and the sentence assembles itself in front of you. Three slots, filled left
 * to right, each one narrowing what the next can be — a gate cannot be "done",
 * only "passed", and a check has no owner at all, so those keys are never
 * offered.
 *
 * ── Why it writes into the input instead of executing ────────────────────────
 *
 * The built sentence is put in the box and nothing else. Every rail the typed
 * path has — the grammar check, the dry run, the confirm card before anything
 * writes, the audit line — is on the other side of that input, and a builder
 * that posted directly would be a second execution path with none of them.
 * This is an input method, not a new way to change the file.
 */

type Row = { ref: string; label: string; kind: 'task' | 'gate' | 'risk' | 'check'; status?: string }
type Vocab = { rows?: Row[]; metrics?: Array<{ key: string; label: string; word: string }> }

/** Straight from services/api/src/commands.ts — the words the parser accepts. */
const STATUS: Record<Row['kind'], string[]> = {
  task: ['done', 'doing', 'todo', 'blocked'],
  gate: ['passed', 'open', 'failed'],
  risk: ['closed', 'open', 'mitigated'],
  check: [],
}

type Verb = {
  id: string
  label: string
  /** What the second slot holds. */
  takes: 'row' | 'metric' | 'text' | null
  /** What the third slot holds, once a row is chosen. */
  then?: 'status' | 'date' | 'name' | 'number' | null
  /** Build the command the parser will see. */
  build: (subject: string, value: string) => string
  /** Kinds this verb can apply to. */
  kinds?: Row['kind'][]
  hint?: string
}

const VERBS: Verb[] = [
  {
    id: 'ask', label: 'Tell me about', takes: 'row', then: null,
    build: (s) => `tell me about ${s}`,
    hint: 'Reads a row. Changes nothing.',
  },
  {
    id: 'status', label: 'Set status', takes: 'row', then: 'status',
    kinds: ['task', 'gate', 'risk'],
    build: (s, v) => `${s} ${v}`,
    hint: 'A check has no status you can set — it is a property of the model.',
  },
  {
    id: 'due', label: 'Set due date', takes: 'row', then: 'date',
    kinds: ['task', 'gate'],
    build: (s, v) => `${s} due ${v}`,
    hint: 'Tasks and gates only. Risks and checks have no due date.',
  },
  {
    id: 'owner', label: 'Set owner', takes: 'row', then: 'name',
    kinds: ['task', 'gate', 'risk'],
    build: (s, v) => `${s} owner ${v}`,
  },
  {
    id: 'metric', label: 'Set a figure', takes: 'metric', then: 'number',
    build: (s, v) => `${s} ${v}`,
    hint: 'Overrides the workbook. The divergence stays visible.',
  },
  {
    id: 'ticket', label: 'Make a ticket', takes: 'text', then: null,
    build: (_s, v) => `ticket ${v}`,
    hint: 'For anything the grammar cannot say. The instruction survives.',
  },
]

export default function CommandBuilder({ onBuild }: { onBuild: (command: string) => void }) {
  const [vocab, setVocab] = useState<Vocab | null>(null)
  const [verb, setVerb] = useState<Verb | null>(null)
  const [subject, setSubject] = useState<Row | null>(null)
  const [metric, setMetric] = useState<string | null>(null)
  const [value, setValue] = useState('')
  const [filter, setFilter] = useState('')

  useEffect(() => {
    void get<Vocab>('/api/voice/vocabulary').then(setVocab).catch(() => setVocab({ rows: [] }))
  }, [])

  const rows = useMemo(() => {
    const all = vocab?.rows ?? []
    const kinds = verb?.kinds
    const ok = kinds ? all.filter((r) => kinds.includes(r.kind)) : all
    const q = filter.trim().toLowerCase()
    return q
      ? ok.filter((r) => `${r.ref} ${r.label}`.toLowerCase().includes(q))
      : ok
  }, [vocab, verb, filter])

  /* The sentence, as it stands. Empty slots read as blanks rather than vanishing,
     so it is obvious what is still missing. */
  const command = useMemo(() => {
    if (!verb) return ''
    const subj = verb.takes === 'metric' ? (metric ?? '') : verb.takes === 'row' ? (subject?.ref ?? '') : ''
    if (verb.takes === 'text') return value.trim() ? verb.build('', value.trim()) : ''
    if (!subj) return ''
    if (verb.then && !value.trim()) return ''
    return verb.build(subj, value.trim())
  }, [verb, subject, metric, value])

  const reset = () => { setVerb(null); setSubject(null); setMetric(null); setValue(''); setFilter('') }

  return (
    <div className="cb">
      <div className="cb-slots">
        <span className={`cb-slot ${verb ? 'on' : ''}`}>{verb?.label ?? 'do what'}</span>
        {verb && verb.takes !== 'text' && (
          <span className={`cb-slot ${subject || metric ? 'on' : ''}`}>
            {verb.takes === 'metric' ? (metric ?? 'which figure') : (subject?.ref ?? 'which row')}
          </span>
        )}
        {verb && (verb.then || verb.takes === 'text') && (
          <span className={`cb-slot ${value ? 'on' : ''}`}>{value || verb.then || 'what to say'}</span>
        )}
        {verb && <button type="button" className="cb-clear" onClick={reset}>clear</button>}
      </div>

      {/* ── Slot 1 ── */}
      {!verb && (
        <div className="cb-keys">
          {VERBS.map((v) => (
            <button key={v.id} type="button" className="cb-key" onClick={() => { setVerb(v); setValue('') }}>
              {v.label}
            </button>
          ))}
        </div>
      )}

      {/*
        * The hint explains what the NEXT slot will and will not offer, so it
        * belongs to the moment of choosing. Left on screen after a row is
        * picked, "a check has no status" reads as a statement about the task
        * you just selected.
        */}
      {verb?.hint && !subject && !metric && <p className="cb-hint">{verb.hint}</p>}

      {/* ── Slot 2: a row ── */}
      {verb?.takes === 'row' && !subject && (
        <>
          <input
            className="cb-filter"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder={`filter ${rows.length} rows`}
            aria-label="Filter rows"
          />
          <div className="cb-keys cb-rows">
            {rows.slice(0, 40).map((r) => (
              <button key={r.ref} type="button" className="cb-key" onClick={() => setSubject(r)} title={r.label}>
                <b>{r.ref}</b> {r.label.slice(0, 44)}{r.label.length > 44 ? '…' : ''}
              </button>
            ))}
            {!rows.length && <p className="cb-hint">Nothing of that kind in the workbook.</p>}
          </div>
        </>
      )}

      {/* ── Slot 2: a figure ── */}
      {verb?.takes === 'metric' && !metric && (
        <div className="cb-keys">
          {(vocab?.metrics ?? []).map((m) => (
            <button key={m.word} type="button" className="cb-key" onClick={() => setMetric(m.word)}>
              {m.label}
            </button>
          ))}
          {!vocab?.metrics?.length && (
            /* The endpoint may not list these; the words are in the grammar either way. */
            ['property value', 'mortgage', 'loan sought', 'score'].map((w) => (
              <button key={w} type="button" className="cb-key" onClick={() => setMetric(w)}>{w}</button>
            ))
          )}
        </div>
      )}

      {/* ── Slot 3 ── */}
      {verb?.then === 'status' && subject && (
        <div className="cb-keys">
          {STATUS[subject.kind].map((w) => (
            <button key={w} type="button" className={`cb-key ${value === w ? 'picked' : ''}`} onClick={() => setValue(w)}>
              {w}
            </button>
          ))}
        </div>
      )}
      {verb?.then === 'date' && subject && (
        <input className="cb-filter" type="date" value={value} onChange={(e) => setValue(e.target.value)} aria-label="Due date" />
      )}
      {verb?.then === 'name' && subject && (
        <input className="cb-filter" value={value} onChange={(e) => setValue(e.target.value)} placeholder="who owns it" aria-label="Owner" />
      )}
      {verb?.then === 'number' && metric && (
        <input className="cb-filter" inputMode="decimal" value={value} onChange={(e) => setValue(e.target.value)} placeholder="a number" aria-label="Value" />
      )}
      {verb?.takes === 'text' && (
        <input className="cb-filter" value={value} onChange={(e) => setValue(e.target.value)} placeholder="what you need" aria-label="Ticket" />
      )}

      {command && (
        <button type="button" className="cb-use" onClick={() => { onBuild(command); reset() }}>
          <code>{command}</code>
          <span>put it in the box</span>
        </button>
      )}
    </div>
  )
}
